import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Menu,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack,
  ArrowForward,
  AttachFile,
  Delete as DeleteIcon,
  Refresh,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { enumLabel } from '../i18n/translate';
import { emailTemplateApi } from '../services/api';
import {
  EMAIL_TEMPLATE_PLACEHOLDERS,
  EmailTemplate,
  EmailTemplateFormData,
  emptyEmailTemplateFormData,
  mapEmailTemplateToFormData,
} from '../types/emailTemplates';
import { DETAIL_FORM_UNIFORM_FIELDS_SX } from '../styles/detailFormStyles';

const EMAIL_TEMPLATES_QUERY_KEY = 'emailTemplates';
const PAGE_SIZE = 10;

export const EmailTemplatesPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [formData, setFormData] = useState<EmailTemplateFormData>(emptyEmailTemplateFormData);
  const [deleteTarget, setDeleteTarget] = useState<EmailTemplate | null>(null);
  const [placeholderMenuAnchor, setPlaceholderMenuAnchor] = useState<HTMLElement | null>(null);
  const bodyFieldRef = useRef<HTMLTextAreaElement | null>(null);

  const templatesQuery = useQuery<EmailTemplate[]>(EMAIL_TEMPLATES_QUERY_KEY, () => emailTemplateApi.getAll());
  const templates = templatesQuery.data ?? [];
  const totalPages = Math.max(1, Math.ceil(templates.length / PAGE_SIZE));
  const paginatedTemplates = templates.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  useEffect(() => {
    if (page > 0 && page >= totalPages) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);

  const availablePlaceholders = useMemo(() => EMAIL_TEMPLATE_PLACEHOLDERS[formData.type], [formData.type]);

  const saveMutation = useMutation(
    () =>
      selectedTemplate
        ? emailTemplateApi.update(selectedTemplate.id, formData)
        : emailTemplateApi.create(formData),
    {
      onSuccess: (saved) => {
        queryClient.invalidateQueries(EMAIL_TEMPLATES_QUERY_KEY);
        toast.success(t('emailTemplates.messages.saved', 'Template saved'));
        setIsCreating(false);
        setSelectedTemplate(saved);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('emailTemplates.messages.saveFailed', 'Failed to save template'));
      },
    }
  );

  const deleteMutation = useMutation((id: number) => emailTemplateApi.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(EMAIL_TEMPLATES_QUERY_KEY);
      toast.success(t('emailTemplates.messages.deleted', 'Template deleted'));
      setDeleteTarget(null);
      setSelectedTemplate(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('emailTemplates.messages.deleteFailed', 'Failed to delete template'));
    },
  });

  const handleStartCreate = () => {
    setSelectedTemplate(null);
    setFormData(emptyEmailTemplateFormData);
    setIsCreating(true);
  };

  const handleSelectTemplate = (template: EmailTemplate) => {
    setIsCreating(false);
    setSelectedTemplate(template);
    setFormData(mapEmailTemplateToFormData(template));
  };

  const insertPlaceholder = (token: string) => {
    const textarea = bodyFieldRef.current;
    if (!textarea) {
      setFormData((prev) => ({ ...prev, body: `${prev.body}{{${token}}}` }));
      setPlaceholderMenuAnchor(null);
      return;
    }
    const start = textarea.selectionStart ?? formData.body.length;
    const end = textarea.selectionEnd ?? formData.body.length;
    const newValue = `${formData.body.slice(0, start)}{{${token}}}${formData.body.slice(end)}`;
    setFormData((prev) => ({ ...prev, body: newValue }));
    setPlaceholderMenuAnchor(null);
    requestAnimationFrame(() => {
      textarea.focus();
      const cursor = start + token.length + 4;
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  const isFormValid = formData.name.trim() && formData.subject.trim() && formData.body.trim();

  return (
    <Box>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 1100,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Toolbar sx={{ pl: { xs: 2, sm: 3 }, pr: { xs: 2, sm: 3 }, minHeight: '48px !important', py: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleStartCreate}
                size="small"
                sx={{ textTransform: 'none' }}
              >
                {t('emailTemplates.newTemplate', 'New Template')}
              </Button>
              <Button
                variant="contained"
                startIcon={saveMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={() => saveMutation.mutate()}
                size="small"
                disabled={(!isCreating && !selectedTemplate) || !isFormValid || saveMutation.isLoading}
                sx={{ textTransform: 'none' }}
              >
                {t('common.save', 'Save')}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => selectedTemplate && setDeleteTarget(selectedTemplate)}
                size="small"
                disabled={!selectedTemplate}
                sx={{ textTransform: 'none' }}
              >
                {t('common.delete', 'Delete')}
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </Box>

      <Container maxWidth="xl" disableGutters sx={{ py: 1 }}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Paper elevation={1}>
              <Toolbar
                sx={{
                  pl: { xs: 2, sm: 3 },
                  pr: { xs: 2, sm: 3 },
                  minHeight: '48px !important',
                  py: 0.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh fontSize="small" />}
                    size="small"
                    onClick={() => templatesQuery.refetch()}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('common.refresh', 'Refresh')}
                  </Button>
                </Box>

                <Box sx={{ flexGrow: 1 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton size="small" onClick={() => setPage((prev) => Math.max(0, prev - 1))} disabled={page === 0}>
                    <ArrowBack fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    {templates.length === 0 ? '0' : page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, templates.length)} {t('common.of', 'of')} {templates.length}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    <ArrowForward fontSize="small" />
                  </IconButton>
                </Box>
              </Toolbar>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('emailTemplates.columns.name', 'Name')}</TableCell>
                      <TableCell>{t('emailTemplates.columns.type', 'Type')}</TableCell>
                      <TableCell>{t('emailTemplates.columns.subject', 'Subject')}</TableCell>
                      <TableCell align="center">{t('emailTemplates.columns.attachPdf', 'Attach PDF')}</TableCell>
                      <TableCell align="center">{t('emailTemplates.columns.active', 'Active')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {templatesQuery.isLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : paginatedTemplates.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            {t('emailTemplates.empty', 'No email templates yet. Create one to start sending order confirmations or invoices by email.')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedTemplates.map((template) => (
                        <TableRow
                          key={template.id}
                          hover
                          selected={selectedTemplate?.id === template.id}
                          onClick={() => handleSelectTemplate(template)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{template.name}</TableCell>
                          <TableCell>
                            <Chip size="small" label={enumLabel(t, 'emailTemplates.types', template.type)} />
                          </TableCell>
                          <TableCell>{template.subject}</TableCell>
                          <TableCell align="center">
                            {template.attachPdf ? <AttachFile fontSize="small" color="primary" /> : null}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              color={template.active ? 'success' : 'default'}
                              label={template.active ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {(isCreating || selectedTemplate) && (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ mt: 2, p: 2, ...DETAIL_FORM_UNIFORM_FIELDS_SX }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {isCreating ? t('emailTemplates.newTemplate', 'New Template') : selectedTemplate?.name}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('emailTemplates.fields.name', 'Name')}
                      value={formData.name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      size="small"
                      label={t('emailTemplates.fields.type', 'Type')}
                      value={formData.type}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, type: e.target.value as EmailTemplateFormData['type'] }))
                      }
                    >
                      <MenuItem value="ORDER_CONFIRMATION">{enumLabel(t, 'emailTemplates.types', 'ORDER_CONFIRMATION')}</MenuItem>
                      <MenuItem value="INVOICE">{enumLabel(t, 'emailTemplates.types', 'INVOICE')}</MenuItem>
                      <MenuItem value="STATUS_UPDATE">{enumLabel(t, 'emailTemplates.types', 'STATUS_UPDATE')}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('emailTemplates.fields.subject', 'Subject')}
                      value={formData.subject}
                      onChange={(e) => setFormData((prev) => ({ ...prev, subject: e.target.value }))}
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('emailTemplates.fields.body', 'Body')}
                      </Typography>
                      <Button size="small" onClick={(e) => setPlaceholderMenuAnchor(e.currentTarget)}>
                        {t('emailTemplates.insertPlaceholder', 'Insert Placeholder')}
                      </Button>
                      <Menu
                        anchorEl={placeholderMenuAnchor}
                        open={Boolean(placeholderMenuAnchor)}
                        onClose={() => setPlaceholderMenuAnchor(null)}
                      >
                        {availablePlaceholders.map((token) => (
                          <MenuItem key={token} onClick={() => insertPlaceholder(token)}>
                            {`{{${token}}}`}
                          </MenuItem>
                        ))}
                      </Menu>
                    </Stack>
                    <TextField
                      fullWidth
                      multiline
                      minRows={8}
                      inputRef={bodyFieldRef}
                      value={formData.body}
                      onChange={(e) => setFormData((prev) => ({ ...prev, body: e.target.value }))}
                      placeholder={t(
                        'emailTemplates.bodyPlaceholder',
                        'Hi {{customerName}}, thank you for your reservation ({{reservationNumber}})...'
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Stack direction="row" spacing={3}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.attachPdf}
                            onChange={(e) => setFormData((prev) => ({ ...prev, attachPdf: e.target.checked }))}
                          />
                        }
                        label={t('emailTemplates.fields.attachPdf', 'Attach PDF automatically')}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.active}
                            onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.checked }))}
                          />
                        }
                        label={t('common.active', 'Active')}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Container>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t('emailTemplates.deleteTitle', 'Delete Template')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('emailTemplates.deleteConfirm', 'Delete "{{name}}"? This cannot be undone.', {
              name: deleteTarget?.name,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t('common.cancel', 'Cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMutation.isLoading}
            onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
          >
            {deleteMutation.isLoading ? <CircularProgress size={20} /> : t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
